import type { ReactNode } from "react";
import { AnimatePresence, motion } from "framer-motion";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X, type LucideIcon } from "lucide-react";

interface Props {
  open: boolean;
  onClose: () => void;
  icon: LucideIcon;
  title: string;
  subtitle?: string;
  children: ReactNode; // <DetailRow/>s
  footer?: ReactNode;  // optional action buttons
}

/** Read-only detail view, sweet-alert styled, with a close button + optional
 * footer actions. Used by every list page's "View details" action. */
export function DetailDialog({ open, onClose, icon: Icon, title, subtitle, children, footer }: Props) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild>
              <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                transition={{ duration: 0.18, ease: "easeOut" }}
                className="fixed inset-0 z-50 bg-black/45 backdrop-blur-md"
              />
            </DialogPrimitive.Overlay>

            <DialogPrimitive.Content asChild>
              <motion.div
                initial={{ opacity: 0, scale: 0.92, y: 12 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.94, y: 6 }}
                transition={{ type: "spring", stiffness: 320, damping: 26, mass: 0.7 }}
                className="fixed left-1/2 top-1/2 z-50 max-h-[85vh] w-full max-w-md -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-3xl border border-border bg-card p-6 shadow-2xl"
              >
                <button
                  onClick={onClose}
                  aria-label="Close"
                  className="absolute right-4 top-4 rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  <X className="size-4" />
                </button>

                <div className="relative mx-auto flex size-16 items-center justify-center">
                  <span className="absolute inset-0 rounded-full bg-primary/15 blur-md" />
                  <span className="relative flex size-14 items-center justify-center rounded-full bg-primary/15 text-primary">
                    <Icon className="size-7" />
                  </span>
                </div>

                <DialogPrimitive.Title asChild>
                  <h2 className="mt-4 text-center text-lg font-bold tracking-tight">{title}</h2>
                </DialogPrimitive.Title>
                {subtitle && (
                  <DialogPrimitive.Description asChild>
                    <p className="mt-1 text-center text-sm text-muted-foreground">{subtitle}</p>
                  </DialogPrimitive.Description>
                )}

                <div className="mt-4 divide-y divide-border rounded-2xl border border-border bg-background/40 px-4">
                  {children}
                </div>

                {footer && <div className="mt-6 flex gap-2">{footer}</div>}
              </motion.div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}

export function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2">
      <span className="shrink-0 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className="text-right text-sm">{value ?? "—"}</span>
    </div>
  );
}
